// src/components/core/EntityStatusBadge.jsx
import React, { memo } from 'react';
import { Chip, useTheme, alpha, Avatar } from '@mui/material';
import PropTypes from 'prop-types';
import {
  taskStatusConfig,
  projectStatusConfig,
  contentStatusConfig,
  invoiceStatusConfig,
  clientStatusConfig,
  brandStatusConfig,
  campaignStatusConfig,
  regionConfig
} from '../../utils/statusConfig';

// Import all status-related icons
import {
  // General status icons
  Edit as DraftIcon,
  Schedule as PlanningIcon,
  PlayArrow as ActiveIcon,
  Pause as HoldIcon,
  CheckCircle as CompletedIcon,
  Cancel as CancelledIcon,
  Campaign as CampaignIcon,
  
  // Task specific icons
  RadioButtonUnchecked as TodoIcon,
  DirectionsRun as InProgressIcon,
  RateReview as ReviewIcon,
  
  // Content specific icons
  HourglassEmpty as PendingIcon,
  Public as PublishedIcon,
  
  // Invoice specific icons
  Send as SentIcon,
  CreditCard as PartialIcon,
  Error as OverdueIcon,
  MoneyOff as WriteOffIcon,
  Assignment as RefundedIcon
} from '@mui/icons-material';

/**
 * Unified status badge component for displaying entity statuses across the application
 * Supports task, project, content, invoice, client, brand, campaign, and region status types
 *
 * @param {Object} props - Component props
 * @param {string} props.status - Status value (e.g., 'to_do', 'active', 'draft', etc.)
 * @param {string} props.entityType - Type of entity ('task', 'project', 'content', 'invoice', 'client', 'brand', 'campaign', 'region')
 * @param {string} props.size - Badge size ('small', 'medium')
 * @param {string} props.variant - Chip variant ('filled', 'outlined')
 * @param {Object} props.icon - Optional override for the icon
 * @param {string} props.label - Optional override for the label
 * @param {string} props.color - Optional override for the color
 * @param {Object} props...rest - Additional props passed to Chip component
 * @returns {JSX.Element} Status badge component
 */
const EntityStatusBadge = ({ 
  status,
  entityType = 'task',
  size = 'medium',
  variant,
  icon: customIcon,
  label: customLabel,
  color: customColor,
  ...rest 
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  
  // Determine automatic variant based on size if not explicitly provided
  const resolvedVariant = variant || (size === 'small' ? 'outlined' : 'filled');
  
  // ========== STATUS CONFIGURATIONS ==========
  
  // Task status configuration
  const taskStatusConfig = {
    to_do: {
      label: 'To Do',
      color: 'default',
      icon: <TodoIcon fontSize="small" />
    },
    in_progress: {
      label: 'In Progress',
      color: 'info',
      icon: <InProgressIcon fontSize="small" />
    },
    in_review: {
      label: 'In Review',
      color: 'warning',
      icon: <ReviewIcon fontSize="small" />
    },
    done: {
      label: 'Done',
      color: 'success',
      icon: <CompletedIcon fontSize="small" />
    },
    cancelled: {
      label: 'Cancelled',
      color: 'error',
      icon: <CancelledIcon fontSize="small" />
    }
  };
  
  // Project status configuration
  const projectStatusConfig = {
    draft: {
      label: 'Draft',
      color: 'default',
      icon: <DraftIcon fontSize="small" />
    },
    planning: {
      label: 'Planning',
      color: 'info',
      icon: <PlanningIcon fontSize="small" />
    },
    active: {
      label: 'Active',
      color: 'success',
      icon: <ActiveIcon fontSize="small" />
    },
    on_hold: {
      label: 'On Hold',
      color: 'warning',
      icon: <HoldIcon fontSize="small" />
    },
    completed: {
      label: 'Completed',
      color: 'primary',
      icon: <CompletedIcon fontSize="small" />
    },
    cancelled: {
      label: 'Cancelled',
      color: 'error',
      icon: <CancelledIcon fontSize="small" />
    }
  };
  
  // Content status configuration
  const contentStatusConfig = {
    draft: {
      label: 'Draft',
      color: 'default',
      icon: <DraftIcon fontSize="small" />
    },
    pendingApproval: {
      label: 'Pending Approval',
      color: 'warning',
      icon: <PendingIcon fontSize="small" />
    },
    approved: {
      label: 'Approved',
      color: 'success',
      icon: <CompletedIcon fontSize="small" />
    },
    rejected: {
      label: 'Rejected',
      color: 'error',
      icon: <CancelledIcon fontSize="small" />
    },
    scheduled: {
      label: 'Scheduled',
      color: 'info',
      icon: <PlanningIcon fontSize="small" />
    },
    published: {
      label: 'Published',
      color: 'success',
      icon: <PublishedIcon fontSize="small" />
    },
    archived: {
      label: 'Archived',
      color: 'default',
      icon: <HoldIcon fontSize="small" />
    }
  };
  
  // Invoice status configuration
  const invoiceStatusConfig = {
    draft: {
      label: 'Draft',
      color: 'default',
      icon: <DraftIcon fontSize="small" />
    },
    pending: {
      label: 'Pending',
      color: 'warning',
      icon: <PendingIcon fontSize="small" />
    },
    sent: {
      label: 'Sent',
      color: 'info',
      icon: <SentIcon fontSize="small" />
    },
    partial: {
      label: 'Partially Paid',
      color: 'primary',
      icon: <PartialIcon fontSize="small" />
    },
    paid: {
      label: 'Paid',
      color: 'success',
      icon: <CompletedIcon fontSize="small" />
    },
    overdue: {
      label: 'Overdue',
      color: 'error',
      icon: <OverdueIcon fontSize="small" />
    },
    cancelled: {
      label: 'Cancelled',
      color: 'default',
      icon: <CancelledIcon fontSize="small" />
    },
    refunded: {
      label: 'Refunded',
      color: 'secondary',
      icon: <RefundedIcon fontSize="small" />
    },
    writeoff: {
      label: 'Written Off',
      color: 'default',
      icon: <WriteOffIcon fontSize="small" />
    }
  };
  
  // Client status configuration
  const clientStatusConfig = {
    active: { 
      label: 'Active', 
      color: 'success',
      icon: <ActiveIcon fontSize="small" />
    },
    inactive: { 
      label: 'Inactive', 
      color: 'error',
      icon: <CancelledIcon fontSize="small" /> 
    },
    onboarding: { 
      label: 'Onboarding', 
      color: 'warning',
      icon: <PendingIcon fontSize="small" />
    },
    paused: { 
      label: 'Paused', 
      color: 'info',
      icon: <HoldIcon fontSize="small" />
    },
    former: { 
      label: 'Former', 
      color: 'default',
      icon: <CancelledIcon fontSize="small" />
    },
  };
  
  // Brand status configuration
  const brandStatusConfig = {
    active: {
      label: 'Active',
      color: 'success',
      icon: <ActiveIcon fontSize="small" />
    },
    inactive: {
      label: 'Inactive',
      color: 'error',
      icon: <CancelledIcon fontSize="small" />
    },
    draft: {
      label: 'Draft',
      color: 'default',
      icon: <DraftIcon fontSize="small" />
    },
  };

  // Form status configuration
  const formStatusConfig = {
    active: {
      label: 'Active',
      color: 'success',
      icon: <ActiveIcon fontSize="small" />
    },
    inactive: {
      label: 'Inactive',
      color: 'error',
      icon: <CancelledIcon fontSize="small" />
    },
    draft: {
      label: 'Draft',
      color: 'default',
      icon: <DraftIcon fontSize="small" />
    }
  };

  // Campaign status configuration is imported from statusConfig.js
  
    // Region configuration is imported from statusConfig.js
  
  // Select the appropriate configuration based on entity type
  const getStatusConfig = () => {
    // Check if this is a region chip
    if (entityType === 'region') {
      return regionConfig[status] || regionConfig.Other;
    }
    
    // Select the appropriate status configuration
    let config;
    switch (entityType) {
      case 'task':
        config = taskStatusConfig[status];
        break;
      case 'project':
        config = projectStatusConfig[status];
        break;
      case 'content':
        config = contentStatusConfig[status];
        break;
      case 'invoice':
        config = invoiceStatusConfig[status];
        break;
      case 'client':
        config = clientStatusConfig[status];
        break;
      case 'brand':
        config = brandStatusConfig[status];
        break;
      case 'campaign':
        config = campaignStatusConfig[status];
        break;
      case 'form':
        config = formStatusConfig[status];
        break;
      default:
        config = null;
    }
    
    // If no config found or custom overrides provided, create a fallback config
    if (!config || customLabel || customColor || customIcon) {
      return {
        label: customLabel || (status && status.replace ? status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown'),
        color: customColor || 'default',
        icon: customIcon || null
      };
    }
    
    return config;
  };
  
  // Get the status configuration
  const config = getStatusConfig();
  
  // ========== ENHANCED STYLING ==========
  
  // Define enhanced styling for better visibility
  const getEnhancedStyling = () => {
    // Handle special case for region chips
    if (entityType === 'region') {
      return {
        backgroundColor: alpha(config.color, 0.1),
        color: config.color,
        fontWeight: theme.aghrba?.tokens?.text?.weight?.medium || 500,
        '& .MuiChip-avatar': {
          color: 'inherit',
          backgroundColor: 'transparent',
        }
      };
    }
    
    // Get base color from theme based on the status config color
    const baseColor = config.color === 'default'
      ? (mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[600])
      : theme.palette[config.color]?.main;
    
    // Enhanced styling for outlined chips (typically small size)
    if (resolvedVariant === 'outlined') {
      return {
        border: `1.5px solid ${baseColor}`,
        backgroundColor: mode === 'dark'
          ? `${baseColor}20` // 12% opacity
          : `${baseColor}10`, // 6% opacity
        color: mode === 'dark'
          ? theme.palette.common.white
          : theme.palette.common.black,
        '& .MuiChip-icon': {
          color: baseColor,
          opacity: mode === 'dark' ? 1 : 0.9,
        },
        '&:hover': {
          backgroundColor: mode === 'dark'
            ? `${baseColor}30` // 19% opacity
            : `${baseColor}15`, // 9% opacity
        },
      };
    }
    
    // Enhanced styling for filled chips
    return {
      backgroundColor: mode === 'dark'
        ? `${baseColor}CC` // 80% opacity for better contrast in dark mode
        : undefined, // Use default in light mode
      color: mode === 'dark'
        ? theme.palette.getContrastText(baseColor)
        : undefined, // Use default in light mode
      fontWeight: theme.aghrba?.tokens?.text?.weight?.semibold || 600,
      boxShadow: mode === 'dark'
        ? `0 2px 4px ${baseColor}40` // 25% opacity shadow
        : undefined,
      '& .MuiChip-icon': {
        color: 'inherit',
        marginRight: theme.spacing(0.5),
      },
      '&:hover': {
        backgroundColor: mode === 'dark'
          ? `${baseColor}E6` // 90% opacity for hover
          : undefined, // Use default in light mode
      },
    };
  };
  
  // Handle region chips differently
  if (entityType === 'region') {
    return (
      <Chip 
        label={status} 
        size={size}
        avatar={<Avatar>{config.icon}</Avatar>}
        sx={{ 
          ...getEnhancedStyling(),
          ...(size === 'small' && theme.aghrba?.tokens?.chips?.sizes?.small),
          ...rest.sx
        }}
        {...rest}
      />
    );
  }
  
  // Render the standard status chip
  return (
    <Chip
      size={size}
      color={config.color || 'default'}
      icon={config.icon}
      label={config.label}
      variant={resolvedVariant}
      sx={{
        ...getEnhancedStyling(),
        ...(size === 'small' && theme.aghrba?.tokens?.chips?.sizes?.small),
        ...rest.sx
      }}
      {...rest}
    />
  );
};

EntityStatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  entityType: PropTypes.oneOf(['task', 'project', 'content', 'invoice', 'client', 'brand', 'campaign', 'region', 'form']),
  size: PropTypes.oneOf(['small', 'medium']),
  variant: PropTypes.oneOf(['filled', 'outlined']),
  icon: PropTypes.element,
  label: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'warning', 'info', 'default']),
};

export default memo(EntityStatusBadge);